/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState } from "react";

import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { theme, ExtendedTooltip, FlexContainer } from "@nordcloud/gnui";
import { Resource } from "~/generated/graphql";
import { TextButton } from "~/components/Styled";
import { StyledSVGIcon } from "~/components/Styled/StyledSVGIcon";
import { GreyText } from "~/components/Tables/styles";
import { dateFormat } from "~/constants/dateFormats";
import { ROUTES } from "~/routing/routes";
import { isEmpty, isNil } from "~/tools";
import { NextPatchingActionsModal } from "./NextPatchingActionsModal";
import { NextPatchingActionTooltip } from "./NextPatchingActionTooltip";

type Props = {
  actions: Resource["futurePatchingActions"];
};

export function NextPatchingActionCell({ actions }: Props) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (isNil(actions) || isEmpty(actions)) {
    return <GreyText>Not Scheduled</GreyText>;
  }

  const hasMultipleActions = actions.length > 1;
  const firstAction = actions[0];
  const formattedDate = dayjs(firstAction.startTime).format(
    dateFormat.dayMonthYearHourMinute
  );

  if (!hasMultipleActions) {
    return (
      <NextPatchingActionTooltip
        startTime={firstAction.startTime}
        scheduleTimezone={firstAction.scheduleTimezone}
      >
        <Link
          to={`${ROUTES.events.generateDetails(firstAction.eventId)}#action=${
            firstAction.planActionId
          }`}
        >
          {formattedDate}
        </Link>
      </NextPatchingActionTooltip>
    );
  }

  return (
    <>
      <FlexContainer
        direction="row"
        gap={theme.spacing.spacing01}
        alignItems="center"
      >
        <TextButton onClick={() => setIsModalOpen(true)} underline>
          <b>({actions.length})</b> {formattedDate}
        </TextButton>

        <ExtendedTooltip caption="Overlapping schedules" status={"danger"}>
          <StyledSVGIcon
            name="danger"
            css={{
              marginLeft: theme.spacing.spacing01,
              color: theme.colors.danger,
            }}
          />
        </ExtendedTooltip>
      </FlexContainer>

      <NextPatchingActionsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        actions={actions}
      />
    </>
  );
}
